'use client';

import { TrendingUp, TrendingDown, Minus, ArrowRight } from 'lucide-react';

// Single large stat highlight
interface StatHighlightProps {
  value: string;
  label: string;
  sublabel?: string;
  trend?: 'up' | 'down' | 'neutral';
}

export function StatHighlight({
  value,
  label,
  sublabel,
  trend,
}: StatHighlightProps) {
  return (
    <div className="my-10 not-prose">
      <div className="relative bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl p-8 text-center">
        {/* Decorative element */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center">
          {trend === 'up' && <TrendingUp className="w-5 h-5 text-white" />}
          {trend === 'down' && <TrendingDown className="w-5 h-5 text-white" />}
          {trend === 'neutral' && <Minus className="w-5 h-5 text-white" />}
          {!trend && (
            <span
              className="text-white text-lg font-bold"
              style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
            >
              #
            </span>
          )}
        </div>

        <p
          className="text-5xl md:text-6xl font-normal text-gray-900 mb-2"
          style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
        >
          {value}
        </p>
        <p className="text-lg font-medium text-gray-700">{label}</p>
        {sublabel && <p className="text-sm text-gray-500 mt-1">{sublabel}</p>}
      </div>
    </div>
  );
}

// Grid of stats
interface Stat {
  value: string;
  label: string;
  trend?: 'up' | 'down' | 'neutral';
}

interface StatsGridProps {
  stats: Stat[];
  columns?: 2 | 3 | 4;
}

export function StatsGrid({ stats, columns = 3 }: StatsGridProps) {
  const gridCols = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
  };

  return (
    <div className="my-10 not-prose">
      <div
        className={`grid ${gridCols[columns]} gap-4 md:gap-6`}
      >
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded-xl p-5 text-center border border-gray-100"
          >
            <div className="flex items-center justify-center gap-2 mb-1">
              <p
                className="text-3xl md:text-4xl font-normal text-gray-900"
                style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
              >
                {stat.value}
              </p>
              {stat.trend && (
                <span
                  className={`${
                    stat.trend === 'up'
                      ? 'text-emerald-500'
                      : stat.trend === 'down'
                        ? 'text-red-500'
                        : 'text-gray-400'
                  }`}
                >
                  {stat.trend === 'up' && <TrendingUp className="w-4 h-4" />}
                  {stat.trend === 'down' && <TrendingDown className="w-4 h-4" />}
                  {stat.trend === 'neutral' && <Minus className="w-4 h-4" />}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Before/After comparison
interface BeforeAfterProps {
  before: {
    value: string;
    label?: string;
  };
  after: {
    value: string;
    label?: string;
  };
  metric: string;
  context?: string;
}

export function BeforeAfter({
  before,
  after,
  metric,
  context,
}: BeforeAfterProps) {
  return (
    <div className="my-10 not-prose">
      <div className="bg-gradient-to-r from-gray-100 via-gray-50 to-emerald-50 rounded-2xl p-6 md:p-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-6 text-center">
          {metric}
        </p>

        <div className="flex items-center justify-center gap-4 md:gap-8">
          {/* Before */}
          <div className="text-center flex-1">
            <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">
              Before
            </p>
            <p
              className="text-3xl md:text-5xl font-normal text-gray-400"
              style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
            >
              {before.value}
            </p>
            {before.label && (
              <p className="text-sm text-gray-500 mt-1">{before.label}</p>
            )}
          </div>

          {/* Arrow */}
          <div className="shrink-0">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-amber-400 flex items-center justify-center">
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
          </div>

          {/* After */}
          <div className="text-center flex-1">
            <p className="text-xs uppercase tracking-wider text-emerald-600 mb-2">
              After
            </p>
            <p
              className="text-3xl md:text-5xl font-normal text-gray-900"
              style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
            >
              {after.value}
            </p>
            {after.label && (
              <p className="text-sm text-gray-600 mt-1">{after.label}</p>
            )}
          </div>
        </div>

        {context && (
          <p className="text-center text-sm text-gray-500 mt-6 pt-4 border-t border-gray-200">
            {context}
          </p>
        )}
      </div>
    </div>
  );
}

// Metric change indicator (inline)
interface MetricChangeProps {
  from: string;
  to: string;
  label?: string;
}

export function MetricChange({ from, to, label }: MetricChangeProps) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm not-prose">
      <span className="text-gray-500 line-through">{from}</span>
      <ArrowRight className="w-3 h-3 text-gray-400" />
      <span className="font-semibold text-gray-900">{to}</span>
      {label && <span className="text-gray-500">({label})</span>}
    </span>
  );
}

// Results summary bar
interface ResultsBarProps {
  results: Array<{ label: string; value: string }>;
}

export function ResultsBar({ results }: ResultsBarProps) {
  return (
    <div className="my-8 not-prose">
      <div className="bg-gray-900 text-white rounded-xl px-6 py-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
        {results.map((result, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="text-gray-400 text-sm">{result.label}:</span>
            <span
              className="text-xl font-normal text-amber-400"
              style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
            >
              {result.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
